import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Search, Filter, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle, Clock, Eye, Brain,
  User, Mail, Phone, Calendar, DollarSign,
  MessageSquare, RefreshCw, X, UserX, Send, MailOpen,
  ScrollText, ClipboardCheck
} from 'lucide-react';
import '../styles/Admin.css';
import ClinicalReportTab from '../components/ClinicalReportTab';
import SessionKitTab from '../components/SessionKitTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Incomplete Intakes Component (Follow-up tracking)
const IncompleteIntakes = () => {
  const { token } = useAuth();
  const [incompleteIntakes, setIncompleteIntakes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);

  const fetchIncompleteIntakes = useCallback(async () => {
    try {
      setLoading(true);
      const [intakesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/intake-followup/admin/incomplete`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/intake-followup/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (intakesRes.ok) {
        const data = await intakesRes.json();
        setIncompleteIntakes(data.drafts || data.incomplete_intakes || []);
      }
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        // Map backend field names to frontend expected names
        setStats({
          total_incomplete: statsData.total_incomplete_intakes || statsData.total_incomplete || 0,
          emails_72h_sent: statsData.followups_72hr_sent || statsData.emails_72h_sent || 0,
          emails_7d_sent: statsData.followups_7day_sent || statsData.emails_7d_sent || 0,
          pending_followups: (statsData.pending_72hr || 0) + (statsData.pending_7day || 0) || statsData.pending_followups || 0
        });
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchIncompleteIntakes();
  }, [fetchIncompleteIntakes]);

  const handleSendFollowup = async (userId, emailType) => {
    setSendingEmail(userId);
    try {
      const response = await fetch(`${API_URL}/api/intake-followup/send-followup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, email_type: emailType })
      });
      
      if (response.ok) {
        fetchIncompleteIntakes();
      }
    } catch (err) {
      console.error('Failed to send follow-up:', err);
    } finally {
      setSendingEmail(null);
    }
  };

  const getProgressPercentage = (intake) => {
    const totalSections = 8;
    const completed = intake.completed_sections?.length || 0;
    return Math.round((completed / totalSections) * 100);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <RefreshCw className="spin" size={32} />
        <p>Loading incomplete intakes...</p>
      </div>
    );
  }

  return (
    <div className="incomplete-intakes-section">
      {/* Stats Cards */}
      {stats && (
        <div className="followup-stats-grid">
          <div className="stat-card incomplete">
            <UserX size={24} />
            <div className="stat-content">
              <span className="stat-value">{stats.total_incomplete || 0}</span>
              <span className="stat-label">Incomplete Intakes</span>
            </div>
          </div>
          <div className="stat-card sent-72h">
            <MailOpen size={24} />
            <div className="stat-content">
              <span className="stat-value">{stats.emails_72h_sent || 0}</span>
              <span className="stat-label">72hr Emails Sent</span>
            </div>
          </div>
          <div className="stat-card sent-7d">
            <Send size={24} />
            <div className="stat-content">
              <span className="stat-value">{stats.emails_7d_sent || 0}</span>
              <span className="stat-label">7-Day Emails Sent</span>
            </div>
          </div>
          <div className="stat-card pending">
            <Clock size={24} />
            <div className="stat-content">
              <span className="stat-value">{stats.pending_followups || 0}</span>
              <span className="stat-label">Pending Follow-ups</span>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete Intakes List */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Progress</th>
              <th>Current Section</th>
              <th>Last Activity</th>
              <th>Follow-up Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {incompleteIntakes.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state-cell">
                  <div className="empty-state-inline">
                    <CheckCircle size={24} />
                    <span>No incomplete intakes! All users have completed their forms.</span>
                  </div>
                </td>
              </tr>
            ) : (
              incompleteIntakes.map((intake) => {
                // Determine email and name - prefer registered user, fall back to guest data
                const displayEmail = intake.user_email || intake.guest_email || intake.form_data?.email || '-';
                const displayName = (intake.user_name && intake.user_name.trim()) 
                  || `${intake.guest_first_name || ''} ${intake.guest_last_name || ''}`.trim()
                  || intake.form_data?.firstName 
                  || 'Unknown';
                const isGuest = intake.is_guest || (!intake.user_email && (intake.guest_email || intake.form_data?.email));
                
                return (
                <tr key={intake.user_id}>
                  <td>
                    <div className="user-info">
                      <span className="user-name">
                        {displayName}
                        {isGuest && <span className="guest-badge">Guest</span>}
                      </span>
                      <span className="user-email">{displayEmail}</span>
                    </div>
                  </td>
                  <td>
                    <div className="progress-cell">
                      <div className="progress-bar-mini">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${getProgressPercentage(intake)}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {intake.completed_sections?.length || 0}/8 sections
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="section-badge">
                      {intake.current_section || 'Not started'}
                    </span>
                  </td>
                  <td>
                    <span className="last-activity">
                      {formatDateTime(intake.last_updated)}
                    </span>
                  </td>
                  <td>
                    <div className="followup-status">
                      {(intake.followup_72hr_sent || intake.followup_72h_sent) && (
                        <span className="status-badge sent">72hr sent</span>
                      )}
                      {(intake.followup_7day_sent || intake.followup_7d_sent) && (
                        <span className="status-badge sent">7-day sent</span>
                      )}
                      {!intake.followup_72hr_sent && !intake.followup_72h_sent && !intake.followup_7day_sent && !intake.followup_7d_sent && (
                        <span className="status-badge pending">Pending</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {!intake.followup_72hr_sent && !intake.followup_72h_sent && (
                        <button
                          className="btn-small primary"
                          onClick={() => handleSendFollowup(intake.user_id, '72h')}
                          disabled={sendingEmail === intake.user_id}
                          title="Send 72-hour follow-up email"
                        >
                          <Mail size={14} />
                          72hr
                        </button>
                      )}
                      {(intake.followup_72hr_sent || intake.followup_72h_sent) && !intake.followup_7day_sent && !intake.followup_7d_sent && (
                        <button
                          className="btn-small secondary"
                          onClick={() => handleSendFollowup(intake.user_id, '7d')}
                          disabled={sendingEmail === intake.user_id}
                          title="Send 7-day follow-up email"
                        >
                          <Mail size={14} />
                          7-day
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Intake List Component
const IntakeList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('completed');
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    package: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  const fetchIntakes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.package) params.append('package', filters.package);
      if (filters.search) params.append('search', filters.search);
      params.append('skip', pagination.skip);
      params.append('limit', pagination.limit);

      const response = await fetch(`${API_URL}/api/admin/intakes?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        throw new Error('Failed to fetch intakes');
      }

      const data = await response.json();
      setIntakes(data.intakes || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filters, pagination.skip, pagination.limit]);

  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      skip: direction === 'next' 
        ? prev.skip + prev.limit 
        : Math.max(0, prev.skip - prev.limit)
    }));
  };

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="admin-btn primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Intake Review Panel</h1>
            <p className="admin-subtitle">
              Review and manage patient health intakes
            </p>
          </div>
        </div>

      {/* Tabs */}
      <div className="intake-tabs">
        <button 
          className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <FileText size={18} />
          Completed Intakes
        </button>
        <button 
          className={`tab-btn ${activeTab === 'incomplete' ? 'active' : ''}`}
          onClick={() => setActiveTab('incomplete')}
        >
          <UserX size={18} />
          Incomplete / Follow-ups
        </button>
      </div>

      {activeTab === 'incomplete' ? (
        <IncompleteIntakes />
      ) : (
        <>
      {/* Filters */}
      <div className="admin-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            data-testid="intake-search-input"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
          data-testid="intake-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="in_review">In Review</option>
          <option value="reviewed">Reviewed</option>
          <option value="assigned">Assigned</option>
        </select>

        <select
          value={filters.package}
          onChange={(e) => handleFilterChange('package', e.target.value)}
          className="filter-select"
          data-testid="intake-package-filter"
        >
          <option value="">All Packages</option>
          <option value="diagnostic-review">Diagnostic Review</option>
          <option value="advocacy-launch">Advocacy Launch</option>
          <option value="deep-dive">Deep Dive</option>
        </select>

        <button onClick={fetchIntakes} className="admin-btn secondary">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Intakes Table */}
      {loading ? (
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading intakes...</p>
        </div>
      ) : intakes.length === 0 ? (
        <div className="admin-empty">
          <FileText size={48} />
          <h3>No intakes found</h3>
          <p>No intakes match your current filters</p>
        </div>
      ) : (
        <>
          <div className="admin-table-container">
            <table className="admin-table" data-testid="intakes-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Package</th>
                  <th>Status</th>
                  <th>AI Analysis</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {intakes.map((intake) => (
                  <tr key={intake.id} data-testid={`intake-row-${intake.id}`}>
                    <td>
                      <div className="patient-cell">
                        <span className="patient-name">{intake.user_name || 'Unknown'}</span>
                        <span className="patient-email">{intake.user_email}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`package-badge ${intake.selected_package}`}>
                        {formatPackage(intake.selected_package)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${intake.status}`}>
                        {getStatusIcon(intake.status)}
                        {formatStatus(intake.status)}
                      </span>
                    </td>
                    <td>
                      {intake.has_ai_analysis ? (
                        <span className="ai-badge has-analysis">
                          <Brain size={14} />
                          {intake.pattern_count} patterns
                        </span>
                      ) : (
                        <span className="ai-badge no-analysis">
                          No analysis
                        </span>
                      )}
                    </td>
                    <td>{formatDate(intake.created_at)}</td>
                    <td>
                      <button
                        onClick={() => navigate(`/admin/intakes/${intake.id}`)}
                        className="action-icon-btn"
                        title="View Details"
                        data-testid={`view-intake-${intake.id}`}
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination">
            <span className="pagination-info">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={pagination.skip === 0}
                className="pagination-btn"
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              <button
                onClick={() => handlePageChange('next')}
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className="pagination-btn"
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </>
      )}
        </>
      )}
      </div>
    </div>
  );
};

// Intake Detail Component
const IntakeDetail = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { intakeId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({ show: false, status: '', notes: '' });

  const fetchIntakeDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/intakes/${intakeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch intake details');
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, intakeId]);

  useEffect(() => {
    fetchIntakeDetail();
  }, [fetchIntakeDetail]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setAddingNote(true);
      const response = await fetch(`${API_URL}/api/admin/intakes/${intakeId}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newNote,
          note_type: 'review',
          is_internal: true
        })
      });

      if (!response.ok) throw new Error('Failed to add note');
      
      setNewNote('');
      fetchIntakeDetail();
    } catch (err) {
      alert('Failed to add note: ' + err.message);
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/intakes/${intakeId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: statusUpdate.status,
          reviewer_notes: statusUpdate.notes
        })
      });

      if (!response.ok) throw new Error('Failed to update status');
      
      setStatusUpdate({ show: false, status: '', notes: '' });
      fetchIntakeDetail();
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading intake details...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error || 'Intake not found'}</p>
          <button onClick={() => navigate('/admin/intakes')} className="admin-btn primary">
            Back to Intakes
          </button>
        </div>
      </div>
    );
  }

  const { intake, patient, admin_notes } = data;
  const formData = intake?.form_data || {};
  const aiAnalysis = intake?.ai_analysis || {};

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin/intakes')} className="back-btn">
              <ChevronLeft size={20} />
              Back to Intakes
            </button>
            <h1>Intake Review</h1>
            <p className="admin-subtitle">
              {intake?.user_name || 'Unknown Patient'} - {formatPackage(intake?.selected_package)}
            </p>
          </div>
          <div className="admin-header-actions">
            <button 
              onClick={() => setStatusUpdate({ show: true, status: intake?.status || 'submitted', notes: '' })}
              className="admin-btn primary"
              data-testid="update-status-btn"
            >
              Update Status
            </button>
          </div>
        </div>

      {/* Status Update Modal */}
      {statusUpdate.show && (
        <div className="admin-modal-overlay" onClick={() => setStatusUpdate({ show: false, status: '', notes: '' })}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Intake Status</h3>
              <button onClick={() => setStatusUpdate({ show: false, status: '', notes: '' })} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <label>New Status</label>
              <select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                className="filter-select full-width"
              >
                <option value="submitted">Submitted</option>
                <option value="in_review">In Review</option>
                <option value="reviewed">Reviewed</option>
                <option value="assigned">Assigned</option>
              </select>
              
              <label>Notes (optional)</label>
              <textarea
                value={statusUpdate.notes}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about this status change..."
                rows={3}
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setStatusUpdate({ show: false, status: '', notes: '' })} className="admin-btn secondary">
                Cancel
              </button>
              <button onClick={handleStatusUpdate} className="admin-btn primary">
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Info Card */}
      <div className="intake-detail-header">
        <div className="patient-info-card">
          <div className="patient-avatar">
            <User size={32} />
          </div>
          <div className="patient-details">
            <h2>{intake?.user_name || 'Unknown'}</h2>
            <div className="patient-meta">
              <span><Mail size={14} /> {intake?.user_email}</span>
              {patient?.phone && <span><Phone size={14} /> {patient.phone}</span>}
            </div>
          </div>
          <div className="intake-meta">
            <span className={`status-badge large ${intake?.status}`}>
              {getStatusIcon(intake?.status)}
              {formatStatus(intake?.status)}
            </span>
            <span className="amount-badge">
              <DollarSign size={16} />
              ${intake?.amount?.toLocaleString() || '0'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'form' ? 'active' : ''}`}
          onClick={() => setActiveTab('form')}
        >
          Form Responses
        </button>
        <button 
          className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          <Brain size={16} />
          AI Analysis
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Admin Notes ({admin_notes?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'smartgaps' ? 'active' : ''}`}
          onClick={() => setActiveTab('smartgaps')}
        >
          <AlertCircle size={16} />
          Smart Gaps
        </button>
        <button
          className={`tab ${activeTab === 'patient-report' ? 'active' : ''}`}
          onClick={() => setActiveTab('patient-report')}
          data-testid="tab-patient-report"
        >
          <ScrollText size={16} />
          Patient Report
        </button>
        <button
          className={`tab ${activeTab === 'session-kit' ? 'active' : ''}`}
          onClick={() => setActiveTab('session-kit')}
          data-testid="tab-session-kit"
        >
          <ClipboardCheck size={16} />
          Call Agenda
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="overview-card">
              <h4>Submission Details</h4>
              <dl>
                <dt>Submitted</dt>
                <dd>{formatDateTime(intake?.created_at)}</dd>
                <dt>Package</dt>
                <dd>{formatPackage(intake?.selected_package)}</dd>
                <dt>Payment Status</dt>
                <dd className={intake?.payment_status === 'paid' ? 'text-success' : 'text-warning'}>
                  {intake?.payment_status === 'paid' ? 'Paid' : 'Pending'}
                </dd>
                {intake?.case_number && (
                  <>
                    <dt>Case Number</dt>
                    <dd>{intake.case_number}</dd>
                  </>
                )}
              </dl>
            </div>
            
            <div className="overview-card">
              <h4>Primary Concern</h4>
              <p className="concern-text">
                {formData.primary_health_concern || 'Not provided'}
              </p>
            </div>

            <div className="overview-card">
              <h4>Current Diagnoses</h4>
              <p className="concern-text">
                {formData.current_diagnoses || 'None listed'}
              </p>
            </div>

            <div className="overview-card">
              <h4>Ideal Outcome</h4>
              <p className="concern-text">
                {formData.ideal_outcome || 'Not provided'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'form' && (
          <div className="form-responses">
            <FormSection title="Personal Information" data={{
              'Full Name': formData.full_name,
              'Date of Birth': formData.date_of_birth,
              'Preferred Contact': formData.preferred_contact,
              'Emergency Contact': formData.emergency_contact
            }} />
            
            <FormSection title="Medical History" data={{
              'Current Diagnoses': formData.current_diagnoses,
              'Past Surgeries': formData.past_surgeries,
              'Current Medications': formData.current_medications,
              'Allergies': formData.allergies
            }} />
            
            <FormSection title="Current Symptoms" data={{
              'Primary Health Concern': formData.primary_health_concern,
              'Symptom Duration': formData.symptom_duration,
              'Symptom Severity': formData.symptom_severity,
              'What Makes It Better': formData.symptom_better,
              'What Makes It Worse': formData.symptom_worse
            }} />

            <FormSection title="Lifestyle" data={{
              'Exercise Frequency': formData.exercise_frequency,
              'Diet Description': formData.diet_description,
              'Sleep Quality': formData.sleep_quality,
              'Stress Level': formData.stress_level
            }} />

            <FormSection title="Goals" data={{
              'Ideal Outcome': formData.ideal_outcome,
              'Previous Treatments Tried': formData.previous_treatments,
              'Additional Information': formData.additional_info
            }} />
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="ai-analysis-section">
            {aiAnalysis.patterns && aiAnalysis.patterns.length > 0 ? (
              <>
                <div className="ai-summary-card">
                  <h4><Brain size={20} /> AI Analysis Summary</h4>
                  <p>{aiAnalysis.summary || 'No summary available'}</p>
                </div>

                <h4 className="section-title">Identified Patterns</h4>
                <div className="patterns-grid">
                  {aiAnalysis.patterns.map((pattern, idx) => (
                    <div key={idx} className={`pattern-card confidence-${getConfidenceLevel(pattern.confidence)}`}>
                      <div className="pattern-header">
                        <span className="pattern-name">{pattern.name}</span>
                        <span className="pattern-confidence">
                          {formatConfidence(pattern.confidence)}
                        </span>
                      </div>
                      <span className="pattern-category">{pattern.category}</span>
                      {pattern.description && (
                        <p className="pattern-description">{pattern.description}</p>
                      )}
                      {pattern.explanation && (
                        <p className="pattern-description">{pattern.explanation}</p>
                      )}
                      {pattern.evidence && pattern.evidence.length > 0 && (
                        <div className="pattern-recommendations">
                          <strong>Evidence:</strong>
                          <ul>
                            {pattern.evidence.map((ev, eIdx) => (
                              <li key={eIdx}>{ev}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {pattern.recommendations && pattern.recommendations.length > 0 && (
                        <div className="pattern-recommendations">
                          <strong>Recommendations:</strong>
                          <ul>
                            {pattern.recommendations.map((rec, rIdx) => (
                              <li key={rIdx}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {pattern.suggested_tests && pattern.suggested_tests.length > 0 && pattern.suggested_tests[0] !== 'N/A' && (
                        <div className="pattern-recommendations">
                          <strong>Suggested Tests:</strong>
                          <ul>
                            {pattern.suggested_tests.map((test, tIdx) => (
                              <li key={tIdx}>{test}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {aiAnalysis.focus_areas && aiAnalysis.focus_areas.length > 0 && (
                  <>
                    <h4 className="section-title">Recommended Focus Areas</h4>
                    <ul className="focus-areas-list">
                      {aiAnalysis.focus_areas.map((area, idx) => (
                        <li key={idx}>{area}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            ) : (
              <div className="admin-empty">
                <Brain size={48} />
                <h3>No AI Analysis Available</h3>
                <p>AI analysis has not been generated for this intake</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="notes-section">
            <div className="add-note-form">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add internal note about this intake..."
                rows={3}
                data-testid="admin-note-input"
              />
              <button 
                onClick={handleAddNote} 
                disabled={addingNote || !newNote.trim()}
                className="admin-btn primary"
                data-testid="add-note-btn"
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>

            {admin_notes && admin_notes.length > 0 ? (
              <div className="notes-list">
                {admin_notes.map((note) => (
                  <div key={note.id} className="note-card">
                    <div className="note-header">
                      <span className="note-author">{note.author_name || 'Unknown'}</span>
                      <span className="note-date">{formatDateTime(note.created_at)}</span>
                    </div>
                    <p className="note-content">{note.content}</p>
                    <span className="note-type">{note.note_type}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty small">
                <MessageSquare size={32} />
                <p>No notes yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'smartgaps' && (
          <SmartGapsView intakeId={intakeId} token={token} />
        )}

        {activeTab === 'patient-report' && (
          data?.case?.id ? (
            <ClinicalReportTab
              caseId={data.case.id}
              token={token}
            />
          ) : (
            <div className="cr-empty-state" style={{ marginTop: '1.5rem' }}>
              <ScrollText size={32} />
              <h4>No case linked yet</h4>
              <p>
                The Patient Report writer activates once this intake has a paid case
                associated with it. Confirm payment and convert the intake to a case to
                enable the AI report.
              </p>
            </div>
          )
        )}

        {activeTab === 'session-kit' && (
          data?.case?.id ? (
            <SessionKitTab caseId={data.case.id} token={token} />
          ) : (
            <div className="cr-empty-state" style={{ marginTop: '1.5rem' }}>
              <ClipboardCheck size={32} />
              <h4>No case linked yet</h4>
              <p>
                The Call Agenda generator activates once this intake has a paid case
                associated with it. It also requires a generated Patient Report —
                its talking points cite the report's findings.
              </p>
            </div>
          )
        )}
      </div>
      </div>
    </div>
  );
};

// Smart Gaps Analysis Component
const SmartGapsView = ({ intakeId, token }) => {
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSmartGaps = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/admin/intakes/${intakeId}/smart-gaps`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch smart gaps analysis');
        }

        const result = await response.json();
        setGaps(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSmartGaps();
  }, [intakeId, token]);

  if (loading) {
    return (
      <div className="smart-gaps-loading">
        <RefreshCw className="spin" size={24} />
        <p>Analyzing documents and intake data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="smart-gaps-error">
        <AlertCircle size={32} />
        <p>{error}</p>
      </div>
    );
  }

  if (!gaps) {
    return (
      <div className="smart-gaps-empty">
        <FileText size={32} />
        <p>No analysis available</p>
      </div>
    );
  }

  const { summary, priority_items, gaps_found, discrepancies, lab_insights, document_supplements, documents_analyzed } = gaps;

  return (
    <div className="smart-gaps-container" data-testid="smart-gaps-view">
      {/* Summary Header */}
      <div className="smart-gaps-summary">
        <div className="summary-stat">
          <span className="stat-value">{documents_analyzed || 0}</span>
          <span className="stat-label">Documents Analyzed</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value warning">{summary?.total_gaps || 0}</span>
          <span className="stat-label">Gaps Found</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value danger">{summary?.total_discrepancies || 0}</span>
          <span className="stat-label">Discrepancies</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value info">{lab_insights?.length || 0}</span>
          <span className="stat-label">Lab Insights</span>
        </div>
      </div>

      {/* Priority Items */}
      {priority_items && priority_items.length > 0 && (
        <div className="smart-gaps-section priority-section">
          <h3>🚨 Priority Items for Clinical Attention</h3>
          <ul className="priority-list">
            {priority_items.map((item, idx) => (
              <li key={idx} className="priority-item">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Lab Insights */}
      {lab_insights && lab_insights.length > 0 && (
        <div className="smart-gaps-section">
          <h3>🔬 Lab Value Insights</h3>
          <div className="lab-insights-grid">
            {lab_insights.map((insight, idx) => (
              <div key={idx} className={`lab-insight-card ${insight.status?.toLowerCase()}`}>
                <div className="lab-header">
                  <span className="lab-name">{insight.lab_name}</span>
                  <span className={`lab-status ${insight.status?.toLowerCase()}`}>{insight.status}</span>
                </div>
                <div className="lab-value">
                  <span className="value">{insight.value}</span>
                  <span className="reference">Ref: {insight.reference_range}</span>
                </div>
                <div className="lab-condition">
                  Associated: <strong>{insight.associated_condition}</strong>
                </div>
                <div className={`lab-awareness ${insight.patient_awareness}`}>
                  Patient {insight.patient_awareness === 'aware' ? '✅ Aware' : '⚠️ Unaware'}
                </div>
                {insight.symptom_correlation && insight.symptom_correlation.length > 0 && (
                  <div className="lab-correlations">
                    <strong>Symptom Correlations:</strong>
                    <ul>
                      {insight.symptom_correlation.map((corr, i) => (
                        <li key={i}>{corr}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Discrepancies */}
      {discrepancies && discrepancies.length > 0 && (
        <div className="smart-gaps-section">
          <h3>⚠️ Discrepancies (Patient Report vs Documents)</h3>
          <div className="discrepancy-cards">
            {discrepancies.map((disc, idx) => (
              <div key={idx} className={`discrepancy-card ${disc.priority?.toLowerCase()}`}>
                <div className="discrepancy-header">
                  <span className="category">{disc.category}</span>
                  <span className={`priority-badge ${disc.priority?.toLowerCase()}`}>{disc.priority}</span>
                </div>
                <div className="discrepancy-content">
                  <div className="patient-says">
                    <strong>Patient Reports:</strong>
                    <p>{disc.patient_reports}</p>
                  </div>
                  <div className="docs-show">
                    <strong>Documents Show:</strong>
                    <p>{disc.documents_show}</p>
                  </div>
                </div>
                <div className="clinical-note">
                  <strong>Clinical Note:</strong> {disc.clinical_note}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gaps Found */}
      {gaps_found && gaps_found.length > 0 && (
        <div className="smart-gaps-section">
          <h3>📋 Intake Gaps (Patient Left Blank, Documents Fill)</h3>
          <div className="gaps-list">
            {gaps_found.map((gap, idx) => (
              <div key={idx} className={`gap-card ${gap.priority?.toLowerCase()}`}>
                <div className="gap-header">
                  <span className="field-name">{gap.field}</span>
                  <span className={`priority-badge ${gap.priority?.toLowerCase()}`}>{gap.priority}</span>
                </div>
                <div className="gap-details">
                  <div className="intake-status">
                    <strong>Intake Status:</strong> {gap.intake_status}
                  </div>
                  <div className="doc-shows">
                    <strong>Documents Show:</strong> {gap.document_shows}
                  </div>
                </div>
                <div className="gap-action">
                  <strong>Action:</strong> {gap.action}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Supplements */}
      {document_supplements && document_supplements.length > 0 && (
        <div className="smart-gaps-section">
          <h3>📄 Additional Document Context</h3>
          <div className="supplements-list">
            {document_supplements.map((supp, idx) => (
              <div key={idx} className="supplement-card">
                <div className="supplement-type">{supp.type}</div>
                <div className="supplement-source">Source: {supp.source}</div>
                <div className="supplement-insight">{supp.insight}</div>
                <div className="supplement-action"><strong>Action:</strong> {supp.action}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Documents Message */}
      {documents_analyzed === 0 && (
        <div className="no-documents-message">
          <FileText size={48} />
          <h4>No Documents Uploaded Yet</h4>
          <p>Patient has not uploaded any medical documents. Smart Gaps analysis works best when lab results, imaging reports, and other medical records are available.</p>
        </div>
      )}
    </div>
  );
};

// Form Section Component
const FormSection = ({ title, data }) => (
  <div className="form-section">
    <h4>{title}</h4>
    <dl>
      {Object.entries(data).map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{key}</dt>
          <dd>{value || 'Not provided'}</dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);

// Helper functions
const formatPackage = (pkg) => {
  const packages = {
    'diagnostic-review': 'Diagnostic Review',
    'advocacy-launch': 'Advocacy Launch',
    'deep-dive': 'Deep Dive'
  };
  return packages[pkg] || pkg || 'Unknown';
};

const formatStatus = (status) => {
  const statuses = {
    'pending_payment': 'Pending Payment',
    'submitted': 'Submitted',
    'in_review': 'In Review',
    'reviewed': 'Reviewed',
    'assigned': 'Assigned'
  };
  return statuses[status] || status || 'Unknown';
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'reviewed':
    case 'assigned':
      return <CheckCircle size={14} />;
    case 'in_review':
      return <Clock size={14} />;
    default:
      return <AlertCircle size={14} />;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getConfidenceLevel = (confidence) => {
  // Handle string values like "High", "Medium", "Low"
  if (typeof confidence === 'string') {
    return confidence.toLowerCase();
  }
  // Handle numeric values
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
};

const formatConfidence = (confidence) => {
  // Handle string values like "High", "Medium", "Low"
  if (typeof confidence === 'string') {
    return `${confidence} confidence`;
  }
  // Handle numeric values
  if (typeof confidence === 'number') {
    return `${Math.round(confidence * 100)}% confidence`;
  }
  return 'Unknown';
};

export { IntakeList, IntakeDetail };
